import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { authApi } from '../services/api';
import { LOGO_URL } from '../constants/brand';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email?.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSuccess(true);
      toast.success('If an account exists with that email, a password reset link has been sent. Please check your inbox.');
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo - clickable, navigates to home */}
        <Link to="/" className="block text-center mb-8 cursor-pointer hover:opacity-90 transition-opacity outline-none focus:outline-none focus-visible:outline-none focus:ring-0">
          <img
            src={LOGO_URL}
            alt="One Globe Alliance"
            className="mx-auto mb-4 h-20 w-auto object-contain"
          />
          <h1 className="text-3xl font-bold text-white">One Globe Alliance</h1>
          <p className="text-blue-200 mt-2">Members Portal</p>
        </Link>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle className="text-xl">Forgot Password</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Enter the email address linked to your account. We will send you a link to reset your password.
            </p>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <p className="text-gray-600 text-center">
                  If an account exists with that email, a password reset link has been sent. Please check your inbox.
                </p>
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Email Address</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="forgot-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="pl-10"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Link
                  to="/login"
                  className="block text-center text-sm text-blue-600 hover:underline mt-4"
                >
                  Back to Login
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-blue-200 text-sm mt-6">
          © 2026 One Globe Alliance. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
